/**
 * Pantalla completa de Notificaciones (OpenSpec 010) — menciones `@nombre`.
 * Reemplaza el popover de la campanita. Figma Gestion>CONVERSACIONES `17261-12330`.
 *
 * El nombre/nº del registro (entityName) y el canal (channelName) se muestran cuando
 * el backend los incluya en AppNotification. Sin ellos, se muestra el label del módulo.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigation } from "@refinedev/core";
import { Avatar, Button, Select, Space, Spin, Switch, Tooltip, Typography, message } from "antd";
import {
  BellOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { io, Socket } from "socket.io-client";
import { useOperatorStores } from "@/hooks/useOperatorStores";
import {
  AppNotification,
  CONVERSATIONS_API_URL,
  isConversationsConfigured,
  conversationsApi,
  getAccessToken,
} from "@/services/conversations.api";
import {
  channelSuffix,
  contextPrefix,
  entityLabel,
  recordHref,
} from "@/services/notification-copy";
import { useBrowserNotifications } from "@/hooks/useBrowserNotifications";

const { Title, Text } = Typography;

const GREEN = "#00733B";
const UNREAD_BG = "#F6FFF9";
const HOVER_BG = "#F5F5F5";
const SECONDARY = "#8C8C8C";
const DIVIDER = "#F0F0F0";

/**
 * `denied` no se puede volver a pedir desde la página: el navegador ignora un segundo
 * `requestPermission()`. Lo único accionable es decir dónde se destraba.
 */
const BLOCKED_HINT =
  "El navegador tiene bloqueadas las notificaciones para este sitio. Se destraba desde el candado de la barra de direcciones, no desde acá.";
const ENABLE_HINT =
  "Te avisa en el escritorio cuando te mencionen, aunque tengas el Admin en otra pestaña. Con el Admin a la vista no interrumpe.";
/**
 * Descartar el prompt (Esc, o un click afuera) deja el permiso en `default`, no en `denied`: no
 * hay nada trabado y mandarlo al candado sería mandarlo a un lugar equivocado. Se vuelve a pedir
 * con sólo tocar el switch de nuevo.
 */
const DISMISSED_HINT =
  "Quedó sin responder el permiso del navegador. Volvé a prenderlo cuando quieras.";

// Paleta de avatares (antd) — color estable por autor.
const AVATAR_COLORS = ["#f56a00", "#7265e6", "#1890ff", "#eb2f96", "#13c2c2", "#d48806", "#1677ff", "#00a2ae"];
function avatarColor(seed: number): string {
  return AVATAR_COLORS[Math.abs(seed) % AVATAR_COLORS.length];
}
function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}

function dayLabel(iso: string): string {
  const d = dayjs(iso);
  if (d.isSame(dayjs(), "day")) return "TODAY";
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "YESTERDAY";
  return d.format("DD MMM YYYY").toUpperCase();
}

export const NotificationsPage = () => {
  const { push } = useNavigation();
  // Mismo criterio que Conversaciones y que el selector de Nuevo Presupuesto: los stores donde el
  // operador PUEDE OPERAR, incluidos los linkeados de sus sellers. Salía del bucket del token, que
  // sólo tiene `store_permission`, así que un operador de seller no podía filtrar por ningún store.
  const { stores } = useOperatorStores();
  const browserNotifications = useBrowserNotifications();
  const [store, setStore] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const load = () => {
    setLoading(true);
    conversationsApi
      .listNotifications({ storeCode: store })
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [store]);

  useEffect(() => {
    // Sin URL del conv-service no hay a dónde conectarse: `""` haría que socket.io y axios
    // peguen contra el dominio del admin, que devuelve el HTML del index.
    if (!isConversationsConfigured()) return;

    const socket = io(`${CONVERSATIONS_API_URL}/conversations`, {
      transports: ["websocket"],
      auth: { token: getAccessToken() },
    });
    socketRef.current = socket;
    socket.on("notification:new", (n: AppNotification) =>
      setItems((prev) => (prev.some((p) => p.notificationId === n.notificationId) ? prev : [n, ...prev])),
    );
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const unreadCount = items.filter((n) => !n.readAt).length;

  const setRead = (n: AppNotification, read: boolean) => {
    setItems((prev) =>
      prev.map((p) => (p.notificationId === n.notificationId ? { ...p, readAt: read ? new Date().toISOString() : null } : p)),
    );
    (read
      ? conversationsApi.markNotificationRead(n.notificationId)
      : conversationsApi.markNotificationUnread(n.notificationId)
    ).catch(() => {});
  };

  const remove = (n: AppNotification) => {
    setItems((prev) => prev.filter((p) => p.notificationId !== n.notificationId));
    conversationsApi.dismissNotification(n.notificationId).catch(() => {});
  };

  const markAll = () => {
    setItems((prev) => prev.map((p) => (p.readAt ? p : { ...p, readAt: new Date().toISOString() })));
    conversationsApi.markAllNotificationsRead().catch(() => {});
  };

  const openRecord = (n: AppNotification) => {
    if (!n.readAt) setRead(n, true);
    const href = recordHref(n);
    if (href) push(href);
  };

  const toggleBrowserNotifications = async (next: boolean) => {
    const permission = await browserNotifications.toggle(next);
    if (!next || permission === "granted") return;
    if (permission === "denied") message.warning(BLOCKED_HINT);
    else message.info(DISMISSED_HINT);
  };

  const groups = useMemo(() => {
    const map = new Map<string, AppNotification[]>();
    for (const n of items) {
      const k = dayLabel(n.createdAt);
      const arr = map.get(k) ?? [];
      arr.push(n);
      map.set(k, arr);
    }
    return [...map.entries()];
  }, [items]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "32px 16px", gap: 24, background: "#fff" }}>
      {/* Título + Store dropdown */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 8px" }}>
        <BellOutlined style={{ fontSize: 24, color: "rgba(0,0,0,0.88)" }} />
        <Title level={3} style={{ margin: 0, color: "rgba(0,0,0,0.88)" }}>Notifications</Title>
        <Select
          style={{ minWidth: 260 }}
          value={store ?? "__all__"}
          onChange={(v) => setStore(v === "__all__" ? undefined : v)}
          options={[
            { value: "__all__", label: "Todos los stores" },
            ...stores.map((s) => ({ value: s.code, label: s.name })),
          ]}
        />
        <Space size={12} style={{ marginLeft: "auto" }}>
          {browserNotifications.supported && (
            <Space size={6}>
              <Switch
                size="small"
                checked={browserNotifications.enabled}
                disabled={browserNotifications.blocked}
                onChange={toggleBrowserNotifications}
              />
              <Text
                style={{
                  fontSize: 13,
                  color: browserNotifications.blocked ? SECONDARY : "#262626",
                }}
              >
                Notificaciones del navegador
              </Text>
              <Tooltip title={browserNotifications.blocked ? BLOCKED_HINT : ENABLE_HINT}>
                <InfoCircleOutlined style={{ color: SECONDARY }} />
              </Tooltip>
            </Space>
          )}
          {unreadCount > 0 && <Button onClick={markAll}>Mark all as viewed</Button>}
        </Space>
      </div>

      {/* Card con la lista */}
      <div style={{ flex: 1, minHeight: 0, border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", overflow: "auto", paddingTop: 16 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : items.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 48 }}>
            <BellOutlined style={{ fontSize: 48, color: "#D9D9D9" }} />
            <div style={{ color: "#262626", marginTop: 12 }}>You don't have any mentions yet.</div>
            <div style={{ color: SECONDARY, fontSize: 13 }}>When someone mentions you with @name in a note, you'll see it here.</div>
          </div>
        ) : (
          groups.map(([label, group]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ padding: "12px 16px 6px" }}>
                <Text strong style={{ fontSize: 12, color: SECONDARY, letterSpacing: 0.5 }}>{label}</Text>
              </div>
              {group.map((n) => {
                const unread = !n.readAt;
                const href = recordHref(n);
                const isHover = hovered === n.notificationId;
                return (
                  <div
                    key={n.notificationId}
                    onMouseEnter={() => setHovered(n.notificationId)}
                    onMouseLeave={() => setHovered((h) => (h === n.notificationId ? null : h))}
                    onClick={() => openRecord(n)}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      padding: "12px 16px",
                      borderTop: `1px solid ${DIVIDER}`,
                      borderLeft: `3px solid ${unread ? GREEN : "transparent"}`,
                      background: isHover ? HOVER_BG : unread ? UNREAD_BG : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Avatar size={32} style={{ backgroundColor: avatarColor(n.actorUserId), flexShrink: 0, fontSize: 12, fontWeight: 600 }}>
                      {initials(n.actorName)}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ fontSize: 14, color: "#262626" }}>
                        <span style={{ fontWeight: 600 }}>{n.actorName}</span> te mencionó en una nota
                      </div>
                      <div style={{ fontSize: 13, color: "#262626" }}>
                        {contextPrefix(n.module)}{" "}
                        {href ? (
                          <a style={{ color: GREEN }} onClick={(e) => { e.stopPropagation(); push(href); }}>
                            {entityLabel(n)}
                          </a>
                        ) : (
                          <span>{entityLabel(n)}</span>
                        )}
                        {channelSuffix(n) && (
                          <span style={{ color: SECONDARY }}>{channelSuffix(n)}</span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 13, fontStyle: "italic", color: SECONDARY,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}
                      >
                        {n.preview}
                      </div>
                    </div>
                    {/* Timestamp + acciones (hover) */}
                    <div style={{ flexShrink: 0, minWidth: 64, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                      <Text style={{ fontSize: 12, color: SECONDARY, whiteSpace: "nowrap" }}>{dayjs(n.createdAt).format("HH:mm")}</Text>
                      {isHover && (
                        <Space size={4}>
                          <Tooltip title={unread ? "Mark as viewed" : "Mark as unread"}>
                            <Button
                              type={unread ? "default" : "text"}
                              size="small"
                              icon={unread ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                              onClick={(e) => { e.stopPropagation(); setRead(n, unread); }}
                            />
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <Button type="text" size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); remove(n); }} />
                          </Tooltip>
                        </Space>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
